import { MutableRefObject, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { createSearchFunctionFromCategory } from "src/utils/categoryUtils";
import { focusToCell, useCellChangeHandler } from "src/utils/grid/GridHandlerContent";
import { TQC1020_R01DetailDto } from "../Dto_TQC1020_R01";

interface UseTQC1020_R01CellChangeProps {
	detailGridRef: MutableRefObject<AgGridReact<TQC1020_R01DetailDto> | null>;
	TQC1020_R01ResultList: TQC1020_R01DetailDto[];
	originalTQC1020_R01ResultList: TQC1020_R01DetailDto[];
	updateTQC1020_R01ResultList: (valueIndex: number, data: TQC1020_R01DetailDto) => void;
	settingInspectorModalProps: (search: string, originalData: TQC1020_R01DetailDto, valueIndex: number) => void;
}

export function useTQC1020_R01CellChange({
	detailGridRef,
	TQC1020_R01ResultList,
	originalTQC1020_R01ResultList,
	updateTQC1020_R01ResultList,
	settingInspectorModalProps,
}: UseTQC1020_R01CellChangeProps) {
	const handleInspectorEmpSearch = useCallback(
		async (newValue: string, rowData: TQC1020_R01DetailDto) => {
			const normalized = String(newValue ?? "").trim();

			if (!normalized) {
				updateTQC1020_R01ResultList(rowData.valueIndex, {
					...rowData,
					inspectorEmp: "",
					inspectorEmpNm: "",
					isModified: !rowData.inserted,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("employee");
			if (!searchFn) {
				settingInspectorModalProps(normalized, rowData, rowData.valueIndex);
				return;
			}

			try {
				const found = await searchFn(normalized.toUpperCase(), (item: any) => {
					updateTQC1020_R01ResultList(rowData.valueIndex, {
						...rowData,
						inspectorEmp: item.empNo ?? "",
						inspectorEmpNm: item.empNm ?? "",
						isModified: !rowData.inserted,
					});
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							focusToCell(detailGridRef, rowData.valueIndex, "inspectDt");
						});
					});
				});

				if (!found) {
					settingInspectorModalProps(normalized, rowData, rowData.valueIndex);
				}
			} catch {
				settingInspectorModalProps(normalized, rowData, rowData.valueIndex);
			}
		},
		[updateTQC1020_R01ResultList, settingInspectorModalProps, detailGridRef],
	);

	const handleCellChange = useCellChangeHandler({
		dataList: TQC1020_R01ResultList,
		originalDataList: originalTQC1020_R01ResultList,
		updateRow: updateTQC1020_R01ResultList,
		customBeforeUpdate: (updatedData, field) => {
			if (field === "inspectQty" || field === "badQty") {
				const inspectQty = Number(updatedData.inspectQty) || 0;
				const badQty = Number(updatedData.badQty) || 0;
				return { ...updatedData, acceptQty: Math.max(0, inspectQty - badQty) };
			}
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, newValue) => {
			if (field === "inspectorEmp") {
				handleInspectorEmpSearch(String(newValue ?? ""), updatedData);
			}
		},
	});

	return { handleCellChange };
}
